import React, { useState, useEffect, useRef } from 'react';
import styles from './ScoreModal.module.css';

const SITE_URL = 'https://speedbattle.vercel.app';

export default function ScoreModal({ isOpen, onClose, onSubmit, scoreData, showToast }) {
  const [name, setName]       = useState('');
  const [phase, setPhase]     = useState('entry'); // 'entry' | 'submitted'
  const [leaderPos, setLeaderPos] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setPhase('entry');
      setName('');
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  if (!isOpen || !scoreData) return null;

  const { type, score, rank, acc, time, percentile } = scoreData;
  const isReaction = type === 'reaction';

  const shareText = isReaction
    ? `⚡ I got ${score}ms reaction speed!\nRank: ${rank?.label} ${rank?.emoji}\nFaster than ${percentile}% of players!\nCan you beat me? 👉 ${SITE_URL}`
    : `⌨️ I typed ${score} WPM with ${acc}% accuracy!\nRank: ${rank?.label} ${rank?.emoji}\nCan you beat me? 👉 ${SITE_URL}`;

  const encodedText = encodeURIComponent(shareText);

  const handleSubmit = async () => {
    const playerName = name.trim() || 'Anonymous';
    const pos = await onSubmit(playerName);
    setLeaderPos(pos);
    setPhase('submitted');
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodedText}`, '_blank');
  };

  const handleTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodedText}`, '_blank');
  };

  const handleInstagram = () => {
    // Copy text first so it's ready to paste in Instagram
    navigator.clipboard?.writeText(shareText).catch(() => {});
    // Show instruction toast
    showToast('📋 Text copied! Opening Instagram — paste in your story or bio');
    // Open Instagram after brief delay so user sees the toast
    setTimeout(() => {
      const isMobile = /iPhone|iPad|Android/i.test(navigator.userAgent);
      if (isMobile) {
        window.location.href = 'instagram://';
        setTimeout(() => { window.open('https://www.instagram.com', '_blank'); }, 1500);
      } else {
        window.open('https://www.instagram.com', '_blank');
      }
    }, 700);
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(shareText).then(() => {
      showToast('📋 Copied to clipboard!');
    });
  };

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({ title: 'SpeedBattle ⚡', text: shareText, url: SITE_URL });
    } else {
      handleCopy();
    }
  };

  return (
    <div className={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className={styles.modal}>

        {/* Glow orb */}
        <div className={styles.glow} style={{ background: `radial-gradient(circle, ${rank?.color ?? '#facc15'}33 0%, transparent 70%)` }} />

        {/* Score hero */}
        <div className={styles.hero}>
          <div className={styles.heroEmoji}>{rank?.emoji}</div>
          <div className={styles.heroScore} style={{ color: rank?.color ?? '#facc15' }}>
            {score}
          </div>
          <div className={styles.heroUnit}>{isReaction ? 'milliseconds' : 'words per minute'}</div>
          <div className={styles.rankBadge} style={{ borderColor: (rank?.color ?? '#facc15') + '88', color: rank?.color ?? '#facc15' }}>
            {rank?.label}
          </div>
          {isReaction
            ? <div className={styles.percentile}>Faster than <b>{percentile}%</b> of players</div>
            : <div className={styles.percentile}>Accuracy <b>{acc}%</b> · Time <b>{time}s</b></div>
          }
        </div>

        <div className={styles.divider} />

        {phase === 'entry' ? (
          <>
            {/* Leaderboard entry */}
            <div className={styles.section}>
              <div className={styles.sectionTitle}>🏆 Save to Leaderboard</div>
              <input
                ref={inputRef}
                className={styles.nameInput}
                placeholder="Enter your name..."
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                maxLength={20}
              />
              <button className={styles.btnPrimary} onClick={handleSubmit}>
                Post Score
              </button>
              <button className={styles.btnSkip} onClick={() => setPhase('submitted')}>
                Skip →
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Submitted confirmation */}
            {leaderPos !== null && leaderPos < 10 && (
              <div className={styles.leaderAlert}>
                🎉 You're <b>#{leaderPos + 1}</b> on the leaderboard!
              </div>
            )}
            {leaderPos !== null && leaderPos >= 10 && (
              <div className={styles.leaderAlert} style={{ color: 'var(--muted)' }}>
                🏅 Score saved!
              </div>
            )}
          </>
        )}

        <div className={styles.divider} />

        {/* Share section */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>🔥 Challenge Friends</div>
          <div className={styles.sharePreview}>{shareText}</div>

          {/* Platform buttons */}
          <div className={styles.shareGrid}>
            <button className={`${styles.shareBtn} ${styles.whatsapp}`} onClick={handleWhatsApp}>
              <svg viewBox="0 0 24 24" className={styles.shareIcon}><path fill="currentColor" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </button>

            <button className={`${styles.shareBtn} ${styles.twitter}`} onClick={handleTwitter}>
              <svg viewBox="0 0 24 24" className={styles.shareIcon}><path fill="currentColor" d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              X / Twitter
            </button>

            <button className={`${styles.shareBtn} ${styles.instagram}`} onClick={handleInstagram}>
              <svg viewBox="0 0 24 24" className={styles.shareIcon}><path fill="currentColor" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
              Instagram
            </button>

            <button className={`${styles.shareBtn} ${styles.copy}`} onClick={handleCopy}>
              <svg viewBox="0 0 24 24" className={styles.shareIcon}><path fill="currentColor" d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
              Copy Link
            </button>
          </div>

          {/* Native share if supported */}
          {typeof navigator !== 'undefined' && navigator.share && (
            <button className={styles.btnNativeShare} onClick={handleNativeShare}>
              ↗ Share via your phone
            </button>
          )}
        </div>

        {/* Close */}
        <button className={styles.closeBtn} onClick={onClose}>✕</button>
      </div>
    </div>
  );
}